export type RtcConnectionState = 'new' | 'connecting' | 'connected' | 'disconnected' | 'failed' | 'closed';
export type ConnectionQuality = 'good' | 'fair' | 'poor';

export function computeQuality(report: RTCStatsReport): ConnectionQuality | null {
  let rtt: number | null = null;
  let loss = 0;
  let sawAnything = false;
  report.forEach((stats) => {
    if (stats.type === 'candidate-pair' && (stats.nominated || stats.selected) && stats.currentRoundTripTime != null) {
      rtt = Math.max(rtt ?? 0, stats.currentRoundTripTime as number);
      sawAnything = true;
    }
    if (stats.type === 'remote-inbound-rtp' && typeof stats.fractionLost === 'number') {
      loss = Math.max(loss, stats.fractionLost as number);
      sawAnything = true;
    }
  });
  if (!sawAnything) return null;
  if (loss > 0.08 || (rtt ?? 0) > 0.45) return 'poor';
  if (loss > 0.03 || (rtt ?? 0) > 0.2) return 'fair';
  return 'good';
}

interface PeerCallbacks {
  onIceCandidate: (candidate: RTCIceCandidateInit) => void;
  onRemoteStream: (stream: MediaStream) => void;
  onConnectionState: (state: RtcConnectionState) => void;
}

let cachedIceServers: RTCIceServer[] | null = null;

export async function fetchIceServers(load: () => Promise<{ iceServers: RTCIceServer[] }>): Promise<RTCIceServer[]> {
  if (cachedIceServers) return cachedIceServers;
  const { iceServers } = await load();
  cachedIceServers = iceServers;
  setTimeout(() => (cachedIceServers = null), 30 * 60 * 1000);
  return iceServers;
}

export class PeerConnection {
  private pc: RTCPeerConnection;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private hasRemoteDescription = false;

  constructor(iceServers: RTCIceServer[], localStream: MediaStream, callbacks: PeerCallbacks) {
    this.pc = new RTCPeerConnection({ iceServers });

    for (const track of localStream.getTracks()) {
      this.pc.addTrack(track, localStream);
    }

    this.pc.onicecandidate = (event) => {
      if (event.candidate) {
        callbacks.onIceCandidate(event.candidate.toJSON());
      }
    };

    this.pc.ontrack = (event) => {
      if (event.streams[0]) {
        callbacks.onRemoteStream(event.streams[0]);
      }
    };

    this.pc.onconnectionstatechange = () => {
      callbacks.onConnectionState(this.pc.connectionState as RtcConnectionState);
    };
  }

  async createOffer(iceRestart = false): Promise<RTCSessionDescriptionInit> {
    const offer = await this.pc.createOffer({ iceRestart });
    await this.pc.setLocalDescription(offer);
    return { type: 'offer', sdp: offer.sdp! };
  }

  async handleOffer(sdp: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> {
    await this.pc.setRemoteDescription(sdp);
    this.hasRemoteDescription = true;
    await this.drainPendingCandidates();
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    return { type: 'answer', sdp: answer.sdp! };
  }

  async handleAnswer(sdp: RTCSessionDescriptionInit): Promise<void> {
    await this.pc.setRemoteDescription(sdp);
    this.hasRemoteDescription = true;
    await this.drainPendingCandidates();
  }

  async addIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!this.hasRemoteDescription) {
      this.pendingCandidates.push(candidate);
      return;
    }
    await this.pc.addIceCandidate(candidate).catch(() => {});
  }

  private async drainPendingCandidates() {
    const pending = this.pendingCandidates.splice(0);
    for (const candidate of pending) {
      await this.pc.addIceCandidate(candidate).catch(() => {});
    }
  }

  get connectionState(): RtcConnectionState {
    return this.pc.connectionState as RtcConnectionState;
  }

  async getQuality(): Promise<ConnectionQuality | null> {
    try {
      return computeQuality(await this.pc.getStats());
    } catch {
      return null;
    }
  }

  close() {
    this.pc.onicecandidate = null;
    this.pc.ontrack = null;
    this.pc.onconnectionstatechange = null;
    this.pc.close();
  }
}
